import React from 'react';
import WName from '../../../../WName';
import PL from '../../../../problems/ProblemLink';
import QL from '../../../../faq/QuestionLink';
import AL from '../../../../angles/AngleLink';
import NW from '../../../../neologisms/NewWord';
import OML from '../../../../ormaybe/OrMaybeLink';
import LRL from '../../../LongReadLink';
import {E1, E2, Qual} from '../../../../common';

// ‘’ … –

export const e_saa = {
  name: "e-saa",
  prelude: <span>
    If we can <i><LRL name="game">agree</LRL></i> that <LRL name="nepantla.sa.E-ma">E<sub><i>म</i></sub> (E-ma) = –⅓</LRL>…
  </span>,
  header: <span>
    What number is E<sub><i>सा</i></sub>?
  </span>,
  outside: <span>
    E<sub><i>सा</i></sub> = (1, 1, 0, 1, 0, 1, 0, 1, …); it’s named that for सामने से भारी (saamane se bhaaree), which is ‘front-heavy’ in <QL name="languages">Hindi</QL>
  </span>,
  preview: <div className="long-read-preview">
    <p>‘We have proved,’ <PL name="ch.">Maya</PL> said, and <QL name="super-racist">shei</QL> wheeled heri <NW>khet</NW> to a blackboard and wrote:</p>
    <p><LRL name="nepantla.sa.E-ma">E<sub><i>म</i></sub> (E-ma) = –⅓</LRL></p>
    <p>‘So that’s <i>negative</i> one-third, right?’ said <PL nme="ch.">Amara</PL>. ‘How to get <i>positive</i> one-third out of that?’</p>
    <p>‘You add one,’ <PL name="ch.">Rugala</PL> said, and then siff frowned. (Yes, Rugala also uses the ‘<QL>Gippetian</QL>’ pronoun system. To, <QL name="polygnosticity">you know</QL>, <PL>torment</PL> the <QL>reader</QL> further.) Siff (obviously <i><PL name="seb ion">seated</PL></i>) said, ‘Oh, oops, that’s +⅔ (plus <i>two</i> thirds). Is there any way to <i>divide</i> in <LRL name="nepantla.">SA</LRL>?’</p>
    <p>‘You can divide by two easily if there’s a zero in front; just remove that zero,’ said Amara. ‘And there <i>is</i> a zero in front if you add one to E<sub><i>म</i></sub> (1, 0, 1, 0, 1, 0, …); to add one, just take that first one-zero (binary 1) and make it zero-one (binary two, read barckwards).’ Sheo wrote:</p>
    <ul>
      <li>E<sub><i>म</i></sub> + 1 =  (0, 1, 1, 0, 1, 0, …) = +⅔</li>
      <li>That ÷ 2 = (1, 1, 0, 1, 0, 1, …) = E<sub><i>सा</i></sub></li>
    </ul>
    <p>‘You can also just multiply E<sub><i>म</i></sub> by two to get –⅔: minus <i>two</i> thirds,’ said Amara. ‘To multiply by two in <LRL name="nepantla.sa.base">binary</LRL> SA is easy; just stick a zero in front: (0, 1, 0, 1, 0, 1, …). And then add one – change that zero you stuck in front to a one – to get +⅓. It’s still E<sub><i>सा</i></sub>.’</p>
    <ul>
      <li>E<sub><i>म</i></sub> + 1 =  (0, 1, 1, 0, 1, 0, …) = –⅔</li>
      <p>E<sub><i>म</i></sub> × 2 = (0, 1, 0, 1, 0, 1, …)</p>    
      <li>That + 1 = (1, 1, 0, 1, 0, 1, …) = E<sub><i>सा</i></sub></li>
    </ul>
  </div>,
  text: <div className="long-read-text">
    <p>‘Here’s where you’re going to <i>want</i> to insist,’ Ishmael said, ‘That this is a “category error”. Sheo went from manipulating infinite repeating sets that represent fractions to adding one just like you’d add one to 42 to get 43. The baffling thing is how <QL>consistent</QL> this is. Sheo can multiply that by three and get one – just like you’d expect.’</p>
    <p>‘<QL name="super-racist">Ii</QL> can do it,’ said Maya, and wrote:</p>
    <p style={{fontFamily: "courier"}}>
E<sub><i>म</i></sub> × E<sub>3</sub>:<br />
110101010…<br />
<u>11000000 ×</u><br />
11010101010…<br />
<u>_1101010101… +</u><br />
10?????…</p>
    <p>‘So drop the one down in the ones place,’ <PL name="ch.">Burt</PL> said, peeking hiso (<NW>khet</NW>’s) horsey face in. ‘Then you have one plus one, so two. Binary 10. Drop the zero and carry the one to the next place. Then zero plus one plus the one you carried: two again. Binary 10. Same thing. And in the next place…’</p>
    <p>‘And for all those question marks (infinitely many of them) it’s the same thing, innit?’ said Lockie. ‘Add zero plus one plus the one you carried and get 10: binary 2. Put a zero and carry to the next one. Repeat forever.’</p>
    <p>‘Technically it’s still a “supertask”,’ Ishmael said. ‘There are infinitely many steps, and you’ll never finish. But you can <i><LRL>understand</LRL></i> it, because it’s well-defined and predictable. The one you keep “carrying” to the next place will never land because there are infinite ones. Similar to how the <LRL name="nepantla.sa.e-omega">successor of E<sub><i>ω</i></sub></LRL> is zero because the carrying never resolves.’</p>
    <p>‘A one with infinite zeroes in the higher place-values,’ Rugala said. ‘Yeah, that’s just E<sub>1</sub>. One.’</p>
    <p>‘And you have completed a “supertask”,’ Ishmael said. ‘מזל טוב (mazel tov).’</p>
  </div>,
};

